// The Logs page's Live tail (APS-540, D120): it polls POST /explore/logs/tail about every 2 s and
// shows the new lines on top of the page's records. Nothing streams: the lines are the stored
// records, a few seconds after the devices sent them. When a line was stored late, behind the
// tail's cursor, an answer sends lines again (`resent`): a line shows once, by its id. The tail stops
// when the page changes, when the query changes (it starts again from now), and after 15 minutes with
// no one on the page; it tries a failed poll again a few times first, and starts again from now after
// a long while hidden.
import { useEffect, useRef, useState } from 'react'
import { isApiError } from '../api/client'
import { fetchExploreLogsTail } from '../api/queries'
import type { ExploreLogRecord, ExploreLogsTailQuery } from '../api/types'

/** How often the tail polls; at once when the API says more lines are waiting. */
export const TAIL_POLL_MS = 2_000
/** No pointer, key or scroll on the page for this long: the tail turns itself off. */
export const TAIL_IDLE_MS = 15 * 60_000
/** Lines the page keeps, newest first. */
export const TAIL_KEEP = 200
/** After a 429 (the API's Retry-After is about 2 s), the tail waits this long, then goes on. */
export const LIMITED_MS = 5_000
/**
 * The ids the tail remembers, newest last. A resent answer carries the API's whole late-line
 * window, its last 120 s of lines, oldest first and cut at 2,000, plus up to 200 new ones. Those
 * 120 s were shown over many answers: at most 200 lines a poll and 40 polls a minute, 16,000 lines.
 * Plus the 2,000, rounded up, so none it showed before comes back (Loom's F1, Gimbal's F1 on the
 * CLI's tail).
 */
export const TAIL_SEEN = 20_000
/** A network error or a 5xx is tried again on the same cursor after these waits, then stops it. */
export const TAIL_RETRY_MS = [2_000, 4_000, 8_000] as const
/**
 * Hidden longer than the API's late-line window (120 s), the tail starts again from now when shown:
 * catching up would poll at once page after page into the poll limit (Loom's N2).
 */
export const TAIL_HIDDEN_MS = 120_000

export interface TailLines {
  /** Newest first, at most TAIL_KEEP. */
  lines: ExploreLogRecord[]
  /** The ids of the last batch, drawn as new. */
  fresh: Set<string>
  /** Lines added since the tail was turned on. */
  added: number
  /** The ids of the last TAIL_SEEN lines added, shown or since dropped, oldest first. */
  seen: readonly string[]
}

export const EMPTY_TAIL: TailLines = { lines: [], fresh: new Set(), added: 0, seen: [] }

/** A batch (oldest first, as the API sends it) on top of what the page shows; a line once. */
export function mergeTail(state: TailLines, batch: readonly ExploreLogRecord[]): TailLines {
  const seen = new Set(state.seen)
  const added: ExploreLogRecord[] = []
  for (const line of batch) {
    if (seen.has(line.id)) continue
    seen.add(line.id)
    added.push(line)
  }
  if (added.length === 0) return { ...state, fresh: new Set() }
  return {
    lines: [...added].reverse().concat(state.lines).slice(0, TAIL_KEEP),
    fresh: new Set(added.map((l) => l.id)),
    added: state.added + added.length,
    seen: state.seen.concat(added.map((l) => l.id)).slice(-TAIL_SEEN),
  }
}

/** The page's records under the tail's lines, without the lines the tail already shows. */
export function withTail(tail: readonly ExploreLogRecord[], records: readonly ExploreLogRecord[]) {
  if (tail.length === 0) return [...records]
  const ids = new Set(tail.map((l) => l.id))
  return [...tail, ...records.filter((r) => !ids.has(r.id))]
}

export type TailStop = 'idle' | 'error'

/**
 * Polls the tail while `on`, for `body` (the page's query without its range). A new body starts
 * over. `stopped` says why it turned itself off; `limited` while a 429 makes it wait.
 */
export function useLiveTail(projectId: string, body: ExploreLogsTailQuery, on: boolean) {
  const key = JSON.stringify(body)
  const [state, setState] = useState<TailLines>(EMPTY_TAIL)
  const [devices, setDevices] = useState(0)
  const [stopped, setStopped] = useState<TailStop | null>(null)
  const [limited, setLimited] = useState(false)
  const bodyRef = useRef(body)
  bodyRef.current = body

  // biome-ignore lint/correctness/useExhaustiveDependencies: the body's key starts a new tail
  useEffect(() => {
    setState(EMPTY_TAIL)
    setDevices(0)
    setLimited(false)
    if (!on) return
    setStopped(null)
    let cursor: string | undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    let active = Date.now()
    let hiddenAt: number | undefined
    let failures = 0
    let alive = true
    const touch = () => {
      active = Date.now()
    }
    const events = ['pointerdown', 'keydown', 'wheel', 'scroll'] as const
    for (const e of events) window.addEventListener(e, touch, { passive: true })
    const next = (ms: number) => {
      if (alive) timer = setTimeout(poll, ms)
    }
    async function poll() {
      if (!alive) return
      if (Date.now() - active > TAIL_IDLE_MS) {
        setStopped('idle')
        return
      }
      // A hidden tab asks nothing; the tail picks up where it was when it is shown again, or
      // starts again from now after TAIL_HIDDEN_MS.
      if (document.visibilityState === 'hidden') {
        hiddenAt ??= Date.now()
        return next(TAIL_POLL_MS)
      }
      if (hiddenAt !== undefined && Date.now() - hiddenAt > TAIL_HIDDEN_MS) {
        cursor = undefined
        setState(EMPTY_TAIL)
      }
      hiddenAt = undefined
      try {
        const answer = await fetchExploreLogsTail(projectId, bodyRef.current, cursor)
        if (!alive) return
        failures = 0
        cursor = answer.next_cursor
        setLimited(false)
        setDevices(answer.devices)
        setState((s) => mergeTail(s, answer.lines))
        next(answer.more ? 0 : TAIL_POLL_MS)
      } catch (error) {
        if (!alive) return
        if (isApiError(error, 429)) {
          setLimited(true)
          return next(LIMITED_MS)
        }
        // A network error or a 5xx (a blip, a deploy) is tried again on the same cursor, waiting
        // longer each time; a 4xx, or the last failure, stops the tail (Loom's N1).
        const wait = TAIL_RETRY_MS[failures]
        if ((!isApiError(error) || error.status >= 500) && wait !== undefined) {
          failures += 1
          return next(wait)
        }
        setStopped('error')
      }
    }
    poll()
    return () => {
      alive = false
      clearTimeout(timer)
      for (const e of events) window.removeEventListener(e, touch)
    }
  }, [projectId, key, on])

  return { ...state, devices, stopped, limited }
}
